import { supabase } from './supabase';
import type { ApiResponse } from './appConfigService';

export interface PerformanceSnapshot {
  id: string;
  created_at: string;
  user_id?: string | null;
  session_id?: string | null;
  platform?: string | null;
  app_version?: string | null;
  is_in_background?: boolean | null;
  active_operations?: number | null;
  pending_operations?: number | null;
  max_concurrent_operations?: number | null;
  ws_connected?: boolean | null;
  realtime_channels?: number | null;
  upload_queue_depth?: number | null;
  metadata?: {
    device?: {
      model?: string | null;
      manufacturer?: string | null;
      osName?: string | null;
      osVersion?: string | null;
      osBuildId?: string | null;
      appState?: string | null;
    };
    network?: {
      type?: string | null;
      quality?: string | null;
      isConnected?: boolean | null;
      isInternetReachable?: boolean | null;
    };
    lifecycle?: {
      phase?: string | null;
      backgroundDurationMs?: number | null;
      appUptimeMs?: number | null;
    };
    requestHealth?: {
      total?: number;
      failed?: number;
      failureRate?: number;
      foregroundShare?: number;
      avgDurationMs?: number;
      p95DurationMsApprox?: number;
      maxDurationMs?: number;
      byEndpoint?: Record<string, unknown>;
      topOtherUrls?: Array<Record<string, unknown>>;
    };
    realtime?: {
      socketState?: string;
      lastFailedChannel?: {
        id: string;
        topic?: string;
        channelState?: string;
        error?: string;
        ts?: number;
      } | null;
      channels?: Array<{
        id: string;
        topic?: string;
        isActive?: boolean;
        channelState?: string;
        lastReceivedAgeMs?: number | null;
        lastError?: { message: string; ts: number } | null;
      }>;
    };
  } | null;
  source?: string | null;
}

export const performanceMonitorService = {
  async getLatestSnapshots(limit = 200): Promise<ApiResponse<PerformanceSnapshot[]>> {
    try {
      const { data, error } = await supabase
        .from('app_performance_snapshots')
        .select('id, created_at, user_id, session_id, platform, app_version, is_in_background, active_operations, pending_operations, max_concurrent_operations, ws_connected, realtime_channels, upload_queue_depth, metadata, source')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, message: error.message };
      }

      return { success: true, data: data ?? [] };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load performance snapshots.' };
    }
  },

  async getSnapshotsPage(limit = 50, offset = 0): Promise<ApiResponse<PerformanceSnapshot[]> & { count?: number | null }> {
    try {
      const { data, error, count } = await supabase
        .from('app_performance_snapshots')
        .select('id, created_at, user_id, session_id, platform, app_version, is_in_background, active_operations, pending_operations, max_concurrent_operations, ws_connected, realtime_channels, upload_queue_depth, metadata, source', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        return { success: false, message: error.message, count };
      }

      return { success: true, data: data ?? [], count };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load performance snapshots.' };
    }
  },

  async clearSnapshots(): Promise<ApiResponse<null>> {
    try {
      const { error } = await supabase
        .from('app_performance_snapshots')
        .delete()
        .not('id', 'is', null);

      if (error) {
        return { success: false, message: error.message };
      }

      return { success: true, data: null };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to clear performance snapshots.' };
    }
  },
};
